import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Heading,
  Badge,
  SimpleGrid,
  Flex,
  Text,
  IconButton,
  Spinner,
  VStack,
  HStack,
  Divider,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiServer,
  FiDatabase,
  FiCpu,
  FiMessageSquare,
  FiCreditCard,
  FiLink,
} from 'react-icons/fi';
import apiClient from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from 'recharts';

// -----------------------------------------------------------------------------
// Type definitions – keep in sync with the backend contract
// -----------------------------------------------------------------------------
export type ComponentStatus = 'UP' | 'DOWN' | 'UNKNOWN';

export interface ComponentHealth {
  status: ComponentStatus;
  response_time_ms: number;
  message?: string;
  [key: string]: any; // Additional arbitrary data (e.g. DB metrics)
}

export interface HealthCheckData {
  overall_status: ComponentStatus;
  components: {
    api: ComponentHealth;
    database: ComponentHealth;
    mqtt: ComponentHealth;
    sms: ComponentHealth;
    payment: ComponentHealth;
    tcp: ComponentHealth;
  };
}

// Utility: color mapping for statuses ------------------------------------------------
const getStatusColor = (status: ComponentStatus) => {
  switch (status) {
    case 'UP':
      return 'green';
    case 'DOWN':
      return 'red';
    case 'UNKNOWN':
    default:
      return 'yellow';
  }
};

// Icons for each component -----------------------------------------------------------
const componentIcons: Record<keyof HealthCheckData['components'], any> = {
  api: FiServer,
  database: FiDatabase,
  mqtt: FiCpu,
  sms: FiMessageSquare,
  payment: FiCreditCard,
  tcp: FiLink,
};

// Metric row helper ------------------------------------------------------------------
interface MetricRowProps {
  label: string;
  display: string | number;
  series?: { time: number; value: number }[];
}

function MetricRow({ label, display, series }: MetricRowProps) {
  const { t } = useTranslation();

  // Helper to prettify/translate labels
  const translate = (key: string) => {
    // If key contains spaces or special chars, assume it's already display-ready
    if (/[^a-zA-Z0-9_.]/.test(key)) return key;

    const translation = t(`system.${key}`, { defaultValue: '' });
    if (translation) return translation;

    // Fallback prettify: snake_case -> Title Case
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const row = (
    <Flex justify="space-between" align="center">
      <Text color="gray.600">{translate(label)}</Text>
      <Text>{display}</Text>
    </Flex>
  );

  // If series present and has more than one point, render sparkline
  if (series && series.length > 1) {
    return (
      <Box>
        {row}
        <ResponsiveContainer width="100%" height={40}>
          <LineChart data={series} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="time" hide />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Line type="monotone" dataKey="value" stroke="#3182ce" dot={false} strokeWidth={2} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  return row;
}

// Component card ---------------------------------------------------------------------
interface ComponentCardProps {
  name: keyof HealthCheckData['components'];
  health: ComponentHealth;
  history: HealthHistoryPoint[];
}

// Helper to flatten numeric metrics and return record of key->number
const flattenNumericMetrics = (obj: any, parentKey = ''): Record<string, number> => {
  const res: Record<string, number> = {};
  Object.entries(obj).forEach(([k, v]) => {
    const keyPath = parentKey ? `${parentKey}.${k}` : k;
    if (typeof v === 'number') {
      res[keyPath] = v;
    } else if (v && typeof v === 'object') {
      // If nested object has single key same as parent, skip duplication
      if (Object.keys(v).length === 1 && Object.keys(v)[0] === k) {
        const val = (v as any)[k];
        if (typeof val === 'number') res[k] = val;
      } else {
        Object.assign(res, flattenNumericMetrics(v, keyPath));
      }
    }
  });
  return res;
};

// Helper to extract value for metric key path from componentHealth
const getMetricValueByPath = (obj: any, path: string): number | undefined => {
  return path.split('.').reduce<any>((acc, key) => (acc ? acc[key] : undefined), obj);
};

const ComponentCard: React.FC<ComponentCardProps> = ({ name, health, history }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const Icon = componentIcons[name];
  const { t } = useTranslation();

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      bg={bg}
      borderColor={borderColor}
      shadow="sm"
    >
      {/* Header */}
      <HStack mb={2} spacing={2} align="center">
        {Icon && <Icon />}
        <Text fontWeight="bold">
          {t(`system.${name}`)}
        </Text>
        <Badge colorScheme={getStatusColor(health.status)}>{t(`system.${health.status}`)}</Badge>
      </HStack>

      <Divider mb={2} />

      <VStack align="stretch" spacing={1} fontSize="sm">
        {/* Metrics display */}
        {((): JSX.Element[] => {
          // For database component, restrict displayed metrics
          let metricEntries: { key: string; raw: any; display: string; seriesKey?: string }[];
          if (name === 'database') {
            const keys = [
              'response_time_ms',
              'active_connections.active_connections',
              'connection_usage.connection_usage_pct',
              'db_size.db_size',
            ];
            metricEntries = keys
              .map((k) => {
                const rawVal = getMetricValueByPath(health, k);
                if (rawVal === undefined) return null;
                let display = '';
                let seriesKey: string | undefined = k;
                console.log("K", k);
                if (k === 'response_time_ms') {
                  display = `${Number(rawVal).toFixed(2)} ms`;
                  seriesKey = 'response_time_ms';
                } else if (k === 'connection_usage.connection_usage_pct') {
                  const num = typeof rawVal === 'string' ? parseFloat(rawVal) : rawVal;
                  seriesKey = 'connection_usage.connection_usage_pct';
                  display = `${num.toFixed(2)} %`;
                } else if (k === 'db_size.db_size') {
                  const num = typeof rawVal === 'string' ? parseFloat(rawVal) : rawVal;
                  display = isNaN(num) ? String(rawVal) : `${num} MB`;
                  seriesKey = undefined;
                } else {
                  display = String(rawVal);
                }
                return { key: k, raw: rawVal, display, seriesKey };
              })
              .filter(Boolean) as any;
          } else {
            metricEntries = Object.entries(flattenNumericMetrics(health)).map(([k, v]) => ({
              key: k,
              raw: v,
              display: `${Number(v).toFixed(2)} ms`,
              seriesKey: k,
            }));
          }

          return metricEntries.map(({ key: metricKey, raw: metricVal, display, seriesKey }) => {
            // Build series only for numeric chart-worthy metrics
            const shouldChart = !!seriesKey;

            const series = shouldChart
              ? (history
                  .map((pt) => {
                    const comp = (pt.data.components as any)[name];
                    let val: any = getMetricValueByPath(comp, seriesKey!);
                    if (typeof val === 'string') val = parseFloat(val);
                    return val !== undefined && typeof val === 'number'
                      ? { time: pt.timestamp, value: val }
                      : null;
                  })
                  .filter(Boolean) as { time: number; value: number }[])
              : undefined;

            // Adjust label to final segment for readability
            let label: string;
            if (metricKey === 'response_time_ms') label = t('system.responseTime');
            else if (metricKey === 'connection_usage.connection_usage_pct') label = t('system.connectionUsage');
            else if (metricKey === 'active_connections.active_connections') label = t('system.activeConnections');
            else if (metricKey === 'db_size.db_size') label = t('system.dbSize');
            else label = metricKey.split('.').pop() || metricKey;

            return <MetricRow key={metricKey} label={label} display={display} series={series} />;
          });
        })()}

        {/* Non-numeric / message metric */}
        {health.message && (
          <MetricRow label={t('system.message')} display={health.message} />
        )}
      </VStack>
    </Box>
  );
};

// Main page component ----------------------------------------------------------------
const REFRESH_INTERVAL = 20_000; // 20 seconds

// History point type
interface HealthHistoryPoint {
  timestamp: number;
  data: HealthCheckData;
}

const SystemHealthPage: React.FC = () => {
  const { token } = useAuthStore();
  const [healthData, setHealthData] = useState<HealthCheckData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<HealthHistoryPoint[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch data function --------------------------------------------------------------
  const fetchHealth = useCallback(async () => {
    try {
      setIsLoading(true);
      if (token) apiClient.client.setToken(token);

      let data: HealthCheckData | undefined;

      // Preferred: use typed SDK call if available
      if ((apiClient as any).system?.getHealth) {
        let startTime = Date.now();
        const resp = await (apiClient as any).system.getHealth();
        data = resp?.data;
        if (data) {
          let apiResponseTime = Date.now() - startTime;
          data.components.api.response_time_ms = apiResponseTime;
          console.log(`System health fetched in ${apiResponseTime}ms`);
        }
      }

      if (data) {
        setHealthData(data);
        setHistory((prev) => [...prev.slice(-49), { timestamp: Date.now(), data }]);
      }
    } catch (err) {
      console.error('Failed to fetch system health', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Initial fetch & polling ----------------------------------------------------------
  useEffect(() => {
    fetchHealth();

    intervalRef.current = setInterval(fetchHealth, REFRESH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchHealth]);

  // -------------------------------------------------------------------------------
  const overallStatusColor = healthData ? getStatusColor(healthData.overall_status) : 'gray';
  const { t } = useTranslation();

  return (
    <Box p={4}>
      {/* Page header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{t('system.systemHealth')}</Heading>
        <Tooltip label="Refresh" hasArrow>
          <IconButton
            aria-label="Refresh"
            icon={<FiRefreshCw />}
            onClick={fetchHealth}
            isLoading={isLoading}
          />
        </Tooltip>
      </Flex>

      {/* Overall status */}
      <HStack mb={4} spacing={3}>
        <Text fontSize="md" fontWeight="bold">
          {t('system.overallStatus')}:
        </Text>
        {isLoading && !healthData ? (
          <Spinner size="sm" />
        ) : (
          <Badge colorScheme={overallStatusColor} fontSize="sm" px={2} py={1} borderRadius="md">
            {t(`system.${healthData?.overall_status ?? 'N/A'}`)}
          </Badge>
        )}
      </HStack>

      {/* Detailed component cards */}
      {isLoading && !healthData ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" />
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {healthData &&
            (Object.keys(healthData.components) as Array<keyof HealthCheckData['components']>).map((key) => (
              <ComponentCard key={key} name={key} health={healthData.components[key]} history={history} />
            ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default SystemHealthPage;


